import { useMemo } from 'react';
import type { LLMTrace } from '../../ai-tracer-integration/types/ai-tracer-types';
import type {
	SortableFields,
	TraceRow,
} from '../llm-traces/trace-table-config';

export type SortDirection = 'asc' | 'desc';

interface UseTraceTableDataProps {
	traces: LLMTrace[];
	isLoading: boolean;
	filters: {
		global: string;
		session: string;
	};
	sorting: {
		field: SortableFields;
		direction: SortDirection;
	};
	pagination: {
		currentPage: number;
		pageSize: number;
	};
	expandedTraces: Set<string>;
}

interface UseTraceTableDataReturn {
	filteredData: TraceRow[];
	paginatedData: TraceRow[];
	displayData: TraceRow[];
	totalPages: number;
}

export const useTraceTableData = ({
	traces,
	isLoading,
	filters,
	sorting,
	pagination,
	expandedTraces,
}: UseTraceTableDataProps): UseTraceTableDataReturn => {
	return useMemo(() => {
		if (isLoading || !traces.length) {
			return {
				filteredData: [],
				paginatedData: [],
				totalPages: 0,
				displayData: [],
			};
		}

		// Helper to get app name from trace metadata (same logic as formatters.app)
		const getAppName = (trace: LLMTrace) => {
			if (trace.metadata && typeof trace.metadata === 'object') {
				const metadata = trace.metadata as Record<string, unknown>;
				return typeof metadata.app === 'string' ? metadata.app : 'Unknown';
			}
			return 'Unknown';
		};

		// Helper to get formatted model name (same logic as formatters.modelName)
		const getFormattedModelName = (trace: LLMTrace) => {
			// First try to get model name from metadata
			if (trace.metadata && typeof trace.metadata === 'object') {
				const metadata = trace.metadata as Record<string, unknown>;
				if (metadata.modelId && typeof metadata.modelId === 'string') {
					// Extract the model name from modelId like "anthropic.claude-3-5-haiku-20241022-v1:0"
					if (metadata.modelId.includes('anthropic.')) {
						return metadata.modelId.replace('anthropic.', '');
					}
					return metadata.modelId;
				}
			}

			// Enhanced fallback parsing for AWS Bedrock ARNs and other formats
			const nameString = trace.model_name || '';

			// Handle AWS Bedrock ARN format: arn:aws:bedrock:region:account:application-inference-profile/model-name
			if (nameString.includes('arn:aws:bedrock')) {
				// Extract everything after the last '/' which should be the model identifier
				const parts = nameString.split('/');
				if (parts.length > 1) {
					const modelPart = parts[parts.length - 1];
					// If it still contains anthropic prefix, remove it
					if (modelPart.includes('anthropic.')) {
						return modelPart.replace('anthropic.', '');
					}
					return modelPart;
				}
				return nameString.split('/').pop() || nameString;
			}

			// Handle direct anthropic model names
			if (nameString.includes('anthropic.')) {
				return nameString.replace('anthropic.', '');
			}

			// Return as-is if no special formatting needed
			return nameString;
		};

		// Apply filters to parent traces only
		const filtered = traces.filter((trace) => {
			if (filters.global === '') {
				// No global filter applied
				const matchesSessionFilter =
					filters.session === '' || trace.session_id === filters.session;
				return matchesSessionFilter;
			}

			const searchTerm = filters.global.toLowerCase();

			// Search only in visible column content using EXACT same formatting as displayed in table cells
			const searchableFields = [
				new Date(trace.created_at).toLocaleString(), // Created At - formatted same as formatters.date
				trace.function_id || '', // Function - text content of badges (revbot, askrev, etc.)
				getFormattedModelName(trace), // Model - formatted/truncated name
				trace.environment, // Environment - text content of colored badges
				trace.status, // Status - text content of colored badges
				trace.total_tokens.toLocaleString(), // Tokens - total shown in breakdown
				`${trace.input_tokens.toLocaleString()} in / ${trace.output_tokens.toLocaleString()} out`, // Tokens breakdown text
				`$${Number.parseFloat(trace.total_cost).toFixed(6)}`, // Cost - formatted as displayed
				trace.latency_ms.toLocaleString(), // Latency - formatted with locale
				trace.session_id, // Session ID - raw value as displayed
				getAppName(trace), // App - name from metadata
				trace.children && trace.children.length > 0
					? trace.children.length.toString()
					: '0', // Children count as displayed
				// Note: Not including expansion toggle or system_prompt, input_messages, output_content as they're not text-searchable
			];

			const matchesGlobalFilter = searchableFields.some((field) =>
				field.toLowerCase().includes(searchTerm),
			);

			const matchesSessionFilter =
				filters.session === '' || trace.session_id === filters.session;

			return matchesGlobalFilter && matchesSessionFilter;
		});

		// Sort the filtered data
		const sorted = [...filtered].sort((a, b) => {
			let aValue: any = a[sorting.field];
			let bValue: any = b[sorting.field];

			// Handle special cases for sorting
			if (sorting.field === 'total_cost') {
				aValue = Number.parseFloat(a.total_cost);
				bValue = Number.parseFloat(b.total_cost);
			} else if (sorting.field === 'created_at') {
				aValue = new Date(a.created_at).getTime();
				bValue = new Date(b.created_at).getTime();
			} else if (typeof aValue === 'string') {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			}

			if (aValue < bValue) {
				return sorting.direction === 'asc' ? -1 : 1;
			}
			if (aValue > bValue) {
				return sorting.direction === 'asc' ? 1 : -1;
			}
			return 0;
		});

		// Pagination on sorted parent traces
		const startIndex = pagination.currentPage * pagination.pageSize;
		const endIndex = startIndex + pagination.pageSize;
		const paginated = sorted.slice(startIndex, endIndex);

		// Create display data with expanded children inline
		const display: TraceRow[] = [];

		paginated.forEach((trace) => {
			display.push({ ...trace, isChild: false });

			// If trace is expanded and has children, add them immediately after the parent
			if (expandedTraces.has(trace.id) && trace.children?.length) {
				trace.children.forEach((child: LLMTrace) => {
					display.push({ ...child, isChild: true });
				});
			}
		});

		return {
			filteredData: sorted,
			paginatedData: paginated,
			totalPages: Math.ceil(sorted.length / pagination.pageSize),
			displayData: display,
		};
	}, [
		isLoading,
		traces,
		filters.global,
		filters.session,
		pagination.currentPage,
		pagination.pageSize,
		expandedTraces,
		sorting.field,
		sorting.direction,
	]);
};
